import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Switch,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel,  
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../components/hooks/useTableSortInit';
import useCurrencyService from '../../../../../components/hooks/useCurrencyService';
import useSelectedTenant from '../../../../../components/hooks/useSelectedTenant';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { 
  DeleteInternationalUSDtoUSDServiceFeeBIZcore, 
  GetInternationalUSDtoUSDServiceFeesBIZcore, 
  UpdateInternationalUSDtoUSDServiceFeeBIZcore 
} from '../../../../../redux/actions/business/core/internationalUSDtoUSDServiceFeesActions';

import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import InternationalUSDtoUSDServiceFeesModal from './InternationalUSDtoUSDServiceFeesModal';

import global from "../../../../../global.scss";

const InternationalUSDtoUSDServiceFeesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core"]);
  const tenantId = useSelectedTenant();
  const { handleShowAmount } = useCurrencyService();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [internationalUSDtoUSDServiceFeesModalOpen, setInternationalUSDtoUSDServiceFeesModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [internationalUSDtoUSDServiceFeesList, setInternationalUSDtoUSDServiceFeesList] = useState([]);
  const [internationalUSDtoUSDServiceFeesTotal, setInternationalUSDtoUSDServiceFeesTotal] = useState(0);

  const [internationalUSDtoUSDServiceFeesMenuOpen, setInternationalUSDtoUSDServiceFeesMenuOpen] = useState(false);
  const [internationalUSDtoUSDServiceFeesMenuAnchor, setInternationalUSDtoUSDServiceFeesMenuAnchor] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    // {
    //   label: "Service Id",
    //   sort: "ref_service_id",
    // },
    {
      label: "Fee name",
      sort: "fee_name",
    },
    {
      label: "Fee amount regular",
      sort: "fee_amount_regular",
    },
    {
      label: "Fee amount priority",
      sort: "fee_amount_priority",
    },
    {
      label: "Markup percent",
      sort: "markup_percent",
    },
    {
      label: "Min",
      sort: "fee_min",
    },
    {
      label: "Max",
      sort: "fee_max",
    },
    {
      label: "Min trx amount",
      sort: "min_trx_amount",
    },
    {
      label: "Max trx amount",
      sort: "max_trx_amount",
    },
    {
      label: "Active",
      sort: "is_active",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    handleGetInternationalUSDtoUSDServiceFees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatInternationalUSDtoUSDServiceFeesList(state.internationalUSDtoUSDServiceFeesList.records || []);
      setInternationalUSDtoUSDServiceFeesTotal(state.internationalUSDtoUSDServiceFeesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatInternationalUSDtoUSDServiceFeesList = (list) => {
    setInternationalUSDtoUSDServiceFeesList(list);
  };

  async function handleGetInternationalUSDtoUSDServiceFees(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetInternationalUSDtoUSDServiceFeesBIZcore(tenantId, page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  const handleSwitchServiceFee = (id, serviceFee, bool) => {
    let obj = {
      // ...serviceFee,
      is_active: bool,
    }
    dispatch(UpdateInternationalUSDtoUSDServiceFeeBIZcore(tenantId, id, obj, setLoading)).then(() => {
      handleGetInternationalUSDtoUSDServiceFees();
    })
  };


  const handleDelete = () => {
    setDeleteConfirmationModalOpen(false);
    dispatch(DeleteInternationalUSDtoUSDServiceFeeBIZcore(tenantId, deleteId, setDeleting)).then(() => {
      handleGetInternationalUSDtoUSDServiceFees().then(() => {
        setDeleteId(null);
      })
    });
  };

  return (
    <Box>

      <ConfirmationModal
        open={deleteConfirmationModalOpen}
        setOpen={setDeleteConfirmationModalOpen}
        title={"Delete " + modalTitle}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete} 
        />

      <InternationalUSDtoUSDServiceFeesModal 
        open={internationalUSDtoUSDServiceFeesModalOpen}
        setOpen={setInternationalUSDtoUSDServiceFeesModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetInternationalUSDtoUSDServiceFees}
        />

      <TopBar
        title={"International USD to USD service fees"}
        search={search}
        searchPlaceholder={"Search id, service id, name or currency"}
        onSearchChange={handleSearchChange}
        button1Text={"Add international USD to USD service fee"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add International USD to USD service fee");
          setInternationalUSDtoUSDServiceFeesModalOpen(!internationalUSDtoUSDServiceFeesModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          internationalUSDtoUSDServiceFeesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No international usd to usd service fee found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                internationalUSDtoUSDServiceFeesList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    {/* <TableCell>{row.ref_service_id || <NA />}</TableCell> */}
                    <TableCell>{row.fee_name || <NA />}</TableCell>
                    <TableCell>{handleShowAmount(row.fee_currency, row.fee_amount_regular || 0)}</TableCell>
                    <TableCell>{handleShowAmount(row.fee_currency, row.fee_amount_priority || 0)}</TableCell>
                    <TableCell>{row.markup_percent?.toString() + "%" || <NA />}</TableCell>
                    <TableCell>{handleShowAmount(row.fee_currency, row.fee_min || 0)}</TableCell>
                    <TableCell>{handleShowAmount(row.fee_currency, row.fee_max || 0)}</TableCell>
                    <TableCell>{handleShowAmount(row.fee_currency, row.min_trx_amount || 0)}</TableCell>
                    <TableCell>
                      {
                        row.max_trx_amount === -1
                        ? "MAXINT"
                        : handleShowAmount(row.fee_currency, row.max_trx_amount || 0)
                      } 
                    </TableCell>
                    <TableCell>
                      <Switch 
                        checked={row.is_active}
                        onChange={(e) => {
                          handleSwitchServiceFee(row.id, row, e.target.checked)
                        }}
                        />
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        deleteId === row.id && deleting
                        ?
                        <CircularProgress size={24}/>
                        :
                        <IconButton
                          onClick={(e) => {
                            setInternationalUSDtoUSDServiceFeesMenuAnchor(e.currentTarget);
                            setInternationalUSDtoUSDServiceFeesMenuOpen(true);
                            setUpdate(row);
                            setModalTitle(row.fee_name || "");
                          }}>
                          <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                        </IconButton>
                      }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={internationalUSDtoUSDServiceFeesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={internationalUSDtoUSDServiceFeesMenuOpen}
        anchorEl={internationalUSDtoUSDServiceFeesMenuAnchor}
        onClose={() => setInternationalUSDtoUSDServiceFeesMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setInternationalUSDtoUSDServiceFeesMenuOpen(false);
            setInternationalUSDtoUSDServiceFeesModalOpen(!internationalUSDtoUSDServiceFeesModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteId(update.id);
            setInternationalUSDtoUSDServiceFeesMenuOpen(false);
            setDeleteConfirmationModalOpen(true);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <DeleteOutlineOutlinedIcon />
            Delete
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default InternationalUSDtoUSDServiceFeesPage;